import { useState, useEffect, useCallback, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getValidToken } from '../utils/auth';
import { isTradingDay } from '../utils/tradingCalendar';

const API_URL = process.env.REACT_APP_API_URL || '';

// Mirrors the live paper bot's shipped config (main.go CheapBotConfig) so
// the panel starts in sync with what's actually running - see the 2026-07-11
// tuning pass (trigger 1.5 / vol spike 5x) and SL wiring (0 = disabled).
const DEFAULTS = {
  spy: { max_entry: '0.15', qty: '3', tp_pct: '100', sl_pct: '0', trigger_ratio: '1.5', vol_spike_ratio: '5' },
  spx: { max_entry: '0.25', qty: '3', tp_pct: '100', sl_pct: '0', trigger_ratio: '1.5', vol_spike_ratio: '5' },
};

function fmtDate(d) {
  if (!d) return null;
  const yyyy = d.getFullYear(), mm = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function fmtElapsed(s) {
  if (s == null) return '0:00';
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

// Digits + at most one decimal point - used for every numeric field so the
// browser's native keyboard/paste can't slip non-numeric text through.
function isNumericInput(v) {
  return v === '' || /^\d*\.?\d*$/.test(v);
}

function NumberField({ label, value, onChange, title, width = '64px' }) {
  return (
    <label className="flex flex-col gap-1" title={title}>
      <span className="text-[10px] text-gray-500 uppercase tracking-wide whitespace-nowrap">{label}</span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => { if (isNumericInput(e.target.value)) onChange(e.target.value); }}
        className="text-xs rounded px-2 py-1 border focus:outline-none focus:border-blue-500"
        style={{ backgroundColor: '#1f2937', color: '#e5e7eb', borderColor: '#374151', width }}
      />
    </label>
  );
}

// Hotel-style range picker: click a start day, then an end day; the span
// between highlights green. Picking an earlier day after a start is already
// set restarts the selection at that day (react-datepicker's built-in
// selectsRange behavior) - so a user can never end up with an end before
// the start. Weekends are disabled since the backtest only ever considers
// trading days; full month/year navigation (dropdowns + arrows) reaches any
// past date.
function RangePicker({ startDate, endDate, onChange }) {
  const pickerRef = useRef(null);
  const label = startDate && endDate
    ? `${fmtDate(startDate)} → ${fmtDate(endDate)}`
    : startDate ? `${fmtDate(startDate)} → ...` : 'Select test period';

  return (
    <>
      <style>{`
        .tdrp-popper { z-index: 9999 !important; }
        .tdrp {
          background-color: #111827 !important;
          border: 1px solid #374151 !important;
          border-radius: 12px !important;
          font-family: inherit !important;
          font-size: 12px !important;
          box-shadow: 0 8px 32px rgba(0,0,0,0.7) !important;
          overflow: hidden !important;
          color: #d1d5db !important;
        }
        .tdrp .react-datepicker__header {
          background-color: #1f2937 !important;
          border-bottom: 1px solid #374151 !important;
          padding: 10px 0 6px !important;
        }
        .tdrp .react-datepicker__current-month { color: #e5e7eb !important; font-size: 13px !important; font-weight: 600 !important; }
        .tdrp .react-datepicker__month-select, .tdrp .react-datepicker__year-select {
          background-color: #1f2937 !important; color: #e5e7eb !important; border: 1px solid #4b5563 !important;
          border-radius: 4px !important; padding: 2px 6px !important; font-size: 12px !important; cursor: pointer !important;
        }
        .tdrp .react-datepicker__navigation-icon::before { border-color: #9ca3af !important; }
        .tdrp .react-datepicker__day-names { background-color: #1f2937 !important; display: flex !important; justify-content: center !important; padding: 0 8px !important; }
        .tdrp .react-datepicker__day-name { color: #6b7280 !important; font-size: 11px !important; width: 2rem !important; line-height: 2rem !important; text-align: center !important; margin: 0 2px !important; }
        .tdrp .react-datepicker__month { background-color: #111827 !important; margin: 6px 8px !important; }
        .tdrp .react-datepicker__week { display: flex !important; justify-content: center !important; }
        .tdrp .react-datepicker__day {
          background-color: transparent !important; color: #ffffff !important; -webkit-text-fill-color: #ffffff !important;
          width: 2rem !important; line-height: 2rem !important; text-align: center !important; margin: 0 2px !important;
          border-radius: 6px !important; transition: background 0.12s !important; font-size: 12px !important;
        }
        .tdrp .react-datepicker__day:hover:not(.react-datepicker__day--disabled) { background-color: #1d4ed8 !important; color: #fff !important; -webkit-text-fill-color: #fff !important; }
        /* Range endpoints - solid blue, same treatment as a single selected day elsewhere in the app */
        .tdrp .react-datepicker__day--range-start, .tdrp .react-datepicker__day--range-end {
          background-color: #2563eb !important; color: #fff !important; -webkit-text-fill-color: #fff !important; font-weight: 700 !important; border-radius: 6px !important;
        }
        /* Everything between the two endpoints - green, per the "hotel reservation" ask */
        .tdrp .react-datepicker__day--in-range:not(.react-datepicker__day--range-start):not(.react-datepicker__day--range-end),
        .tdrp .react-datepicker__day--in-selecting-range:not(.react-datepicker__day--range-start) {
          background-color: #16a34a55 !important; color: #fff !important; -webkit-text-fill-color: #fff !important; border-radius: 0 !important;
        }
        .tdrp .react-datepicker__day--today:not(.react-datepicker__day--range-start):not(.react-datepicker__day--range-end) {
          border: 1px solid #2563eb !important; color: #60a5fa !important; -webkit-text-fill-color: #60a5fa !important;
        }
        .tdrp .react-datepicker__day--disabled, .tdrp .react-datepicker__day--disabled:hover {
          background-color: transparent !important; color: #4b5563 !important; -webkit-text-fill-color: #4b5563 !important;
          text-decoration: line-through !important; cursor: not-allowed !important;
        }
        .tdrp .react-datepicker__day--outside-month { color: #374151 !important; -webkit-text-fill-color: #374151 !important; }
        .tdrp .react-datepicker__triangle::before { border-bottom-color: #374151 !important; }
        .tdrp .react-datepicker__triangle::after { border-bottom-color: #111827 !important; }
      `}</style>
      <DatePicker
        ref={pickerRef}
        selectsRange
        startDate={startDate}
        endDate={endDate}
        onChange={onChange}
        filterDate={isTradingDay}
        maxDate={new Date()}
        dateFormat="MM/dd/yyyy"
        calendarClassName="tdrp"
        popperClassName="tdrp-popper"
        popperPlacement="bottom-start"
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        customInput={
          <input
            readOnly
            className="text-xs rounded px-2 py-1 border focus:outline-none focus:border-blue-500 cursor-pointer font-mono"
            style={{ backgroundColor: '#1f2937', color: '#e5e7eb', borderColor: '#374151', width: '190px' }}
            value={label}
            onChange={() => {}}
          />
        }
      />
    </>
  );
}

// Control panel for the interactive backtest: date-range picker, on-demand
// data caching ("Load Data"), and a live-parameterized, stoppable run
// ("Start"/"Stop"). One instance per variant (spy/spx) - completely
// independent state, so both panels can load/run at the same time.
export default function BacktestControlPanel({ variant, onResultsUpdated }) {
  const [range, setRange] = useState([null, null]);
  const [startDate, endDate] = range;
  const [params, setParams] = useState(DEFAULTS[variant]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [dataStatus, setDataStatus] = useState(null);
  const [loadJob, setLoadJob] = useState(null);
  const [testJob, setTestJob] = useState(null);
  const [confirmOverride, setConfirmOverride] = useState(null);
  const [error, setError] = useState(null);

  const authedFetch = useCallback(async (path, opts) => {
    const token = await getValidToken();
    return fetch(`${API_URL}${path}`, {
      ...opts,
      headers: { ...(opts?.headers || {}), Authorization: `Bearer ${token}` },
    });
  }, []);

  const refreshDataStatus = useCallback(async () => {
    if (!startDate || !endDate) { setDataStatus(null); return; }
    try {
      const res = await authedFetch(`/api/cheap-backtest/data-status?variant=${variant}&from=${fmtDate(startDate)}&to=${fmtDate(endDate)}`);
      if (res.ok) setDataStatus(await res.json());
    } catch { /* transient - next poll/change retries */ }
  }, [startDate, endDate, variant, authedFetch]);

  useEffect(() => { refreshDataStatus(); }, [refreshDataStatus]);

  // Resume tracking a job that's already running server-side (e.g. this
  // page was refreshed mid-load or mid-run).
  useEffect(() => {
    (async () => {
      const lr = await authedFetch(`/api/cheap-backtest/load-status?variant=${variant}`);
      if (lr.ok) { const s = await lr.json(); if (s.running) setLoadJob(s); }
      const rr = await authedFetch(`/api/cheap-backtest/run-status?variant=${variant}`);
      if (rr.ok) { const s = await rr.json(); if (s.running) setTestJob(s); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  useEffect(() => {
    if (!loadJob?.running) return;
    const id = setInterval(async () => {
      const res = await authedFetch(`/api/cheap-backtest/load-status?variant=${variant}`);
      if (res.ok) {
        const s = await res.json();
        setLoadJob(s);
        if (s.done) refreshDataStatus();
      }
    }, 1500);
    return () => clearInterval(id);
  }, [loadJob?.running, variant, authedFetch, refreshDataStatus]);

  useEffect(() => {
    if (!testJob?.running) return;
    const id = setInterval(async () => {
      const res = await authedFetch(`/api/cheap-backtest/run-status?variant=${variant}`);
      if (res.ok) {
        const s = await res.json();
        setTestJob(s);
        if (s.done) onResultsUpdated?.();
      }
    }, 1500);
    return () => clearInterval(id);
  }, [testJob?.running, variant, authedFetch, onResultsUpdated]);

  const handleRangeChange = (dates) => {
    setRange(dates);
    setDataStatus(null);
    setConfirmOverride(null);
    setError(null);
  };

  const startLoad = async () => {
    setError(null);
    const res = await authedFetch('/api/cheap-backtest/load-data', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variant, from: fmtDate(startDate), to: fmtDate(endDate) }),
    });
    if (res.status === 202) {
      setLoadJob({ running: true, elapsed_s: 0, days_done: 0, days_total: 0, done: false, error: '' });
    } else {
      const body = await res.json().catch(() => ({}));
      setError(body.error || 'Failed to start data load');
    }
  };

  const paramsForRequest = () => ({
    max_entry: parseFloat(params.max_entry) || 0,
    qty: parseInt(params.qty, 10) || 0,
    tp_pct: parseFloat(params.tp_pct) || 0,
    sl_pct: parseFloat(params.sl_pct) || 0,
    trigger_ratio: parseFloat(params.trigger_ratio) || 0,
    vol_spike_ratio: parseFloat(params.vol_spike_ratio) || 0,
  });

  const startTest = async (force) => {
    setError(null);
    const res = await authedFetch('/api/cheap-backtest/run', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variant, from: fmtDate(startDate), to: fmtDate(endDate), params: paramsForRequest(), force }),
    });
    if (res.status === 202) {
      setConfirmOverride(null);
      setTestJob({ running: true, elapsed_s: 0, days_done: 0, days_total: 0, done: false, error: '' });
    } else if (res.status === 409) {
      const body = await res.json();
      setConfirmOverride(body.existing_trades);
    } else {
      const body = await res.json().catch(() => ({}));
      setError(body.error || 'Failed to start test');
      setConfirmOverride(null);
    }
  };

  const stopTest = async () => {
    await authedFetch('/api/cheap-backtest/stop', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variant }),
    });
  };

  const rangeSelected = !!(startDate && endDate);
  const dataReady = !!(dataStatus && dataStatus.total_days > 0 && dataStatus.missing_days.length === 0);
  const loadRunning = !!loadJob?.running;
  const testRunning = !!testJob?.running;

  const loadDisabled = !rangeSelected || dataReady || loadRunning || testRunning;
  const startDisabled = !rangeSelected || !dataReady || loadRunning;

  const btnStyle = (disabled, color = '#1d4ed8') => ({
    backgroundColor: disabled ? '#374151' : color,
    color: disabled ? '#6b7280' : '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer',
  });

  return (
    <div className="bg-gray-900 rounded-2xl p-4 mb-3 border" style={{ borderColor: '#1f2937' }}>
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide">Test Period</span>
          <RangePicker startDate={startDate} endDate={endDate} onChange={handleRangeChange} />
        </div>

        <NumberField label="Max Price ($)" value={params.max_entry}
          onChange={(v) => setParams((p) => ({ ...p, max_entry: v }))}
          title="Never pay more than this per contract - multiplied by 100 for the real trade cost" />
        <NumberField label="Qty" value={params.qty} width="48px"
          onChange={(v) => setParams((p) => ({ ...p, qty: v }))} />
        <NumberField label="TP %" value={params.tp_pct} width="56px"
          onChange={(v) => setParams((p) => ({ ...p, tp_pct: v }))}
          title="Take profit at entry + this percent" />
        <NumberField label="SL %" value={params.sl_pct} width="56px"
          onChange={(v) => setParams((p) => ({ ...p, sl_pct: v }))}
          title="Stop loss at entry - this percent. 0 = disabled" />

        <button type="button" onClick={() => setShowAdvanced((s) => !s)}
          className="text-xs text-blue-400 hover:text-blue-300 pb-1.5">
          {showAdvanced ? 'Hide advanced' : 'Advanced'}
        </button>

        <div className="flex-1" />

        <div className="flex items-center gap-2">
          {dataStatus && !loadRunning && (dataReady ? (
            <span className="text-xs font-semibold" style={{ color: '#22c55e' }}>Data exists</span>
          ) : (
            <span className="text-xs font-semibold" style={{ color: '#ef4444' }}>
              Data need to be Loaded ({dataStatus.cached_days}/{dataStatus.total_days} days)
            </span>
          ))}
          {loadRunning && (
            <span className="text-xs font-mono" style={{ color: '#60a5fa' }}>
              {fmtElapsed(loadJob.elapsed_s)} · day {loadJob.days_done}/{loadJob.days_total}
            </span>
          )}
          {loadJob?.done && !loadJob.running && (
            loadJob.error ? (
              <span className="text-xs font-semibold cursor-help" style={{ color: '#ef4444' }} title={loadJob.error}>
                Load error (hover)
              </span>
            ) : (
              <span className="text-xs font-semibold" style={{ color: '#22c55e' }}>Loaded ({fmtElapsed(loadJob.elapsed_s)})</span>
            )
          )}
          <button type="button" onClick={startLoad} disabled={loadDisabled}
            className="text-xs px-3 py-1.5 rounded font-semibold" style={btnStyle(loadDisabled)}>
            Load Data
          </button>
        </div>

        <div className="flex items-center gap-2">
          {testRunning && (
            <span className="text-xs font-mono" style={{ color: '#60a5fa' }}>
              {fmtElapsed(testJob.elapsed_s)} · day {testJob.days_done}/{testJob.days_total}
            </span>
          )}
          {testJob?.done && !testJob.running && (
            testJob.error ? (
              <span className="text-xs font-semibold cursor-help" style={{ color: '#ef4444' }} title={testJob.error}>
                Run error (hover)
              </span>
            ) : (
              <span className="text-xs font-semibold" style={{ color: testJob.stopped ? '#f59e0b' : '#22c55e' }}>
                {testJob.stopped ? 'Stopped' : 'Done'} ({fmtElapsed(testJob.elapsed_s)})
              </span>
            )
          )}
          <button
            type="button"
            onClick={() => (testRunning ? stopTest() : startTest(false))}
            disabled={!testRunning && startDisabled}
            className="text-xs px-3 py-1.5 rounded font-semibold"
            style={testRunning ? { backgroundColor: '#dc2626', color: '#fff', cursor: 'pointer' } : btnStyle(startDisabled, '#16a34a')}
          >
            {testRunning ? 'Stop' : 'Start'}
          </button>
        </div>
      </div>

      {showAdvanced && (
        <div className="flex gap-4 mt-3 pt-3 border-t" style={{ borderColor: '#1f2937' }}>
          <NumberField label="Trigger Ratio" value={params.trigger_ratio} width="64px"
            onChange={(v) => setParams((p) => ({ ...p, trigger_ratio: v }))}
            title="Mid must reach this multiple of the local low to confirm ignition" />
          <NumberField label="Vol Spike x" value={params.vol_spike_ratio} width="64px"
            onChange={(v) => setParams((p) => ({ ...p, vol_spike_ratio: v }))}
            title="1-min volume must reach this multiple of its trailing average" />
        </div>
      )}

      {confirmOverride != null && (
        <div className="mt-3 p-3 rounded-lg flex items-center gap-3 flex-wrap"
          style={{ backgroundColor: '#7c2d1220', border: '1px solid #7c2d12' }}>
          <span className="text-xs" style={{ color: '#fbbf24' }}>
            Previous results exist for this period ({confirmOverride} trade{confirmOverride === 1 ? '' : 's'}) - override?
          </span>
          <button type="button" onClick={() => startTest(true)}
            className="text-xs px-3 py-1 rounded font-semibold" style={{ backgroundColor: '#16a34a', color: '#fff' }}>
            Yes
          </button>
          <button type="button" onClick={() => setConfirmOverride(null)}
            className="text-xs px-3 py-1 rounded font-semibold" style={{ backgroundColor: '#4b5563', color: '#fff' }}>
            No
          </button>
        </div>
      )}

      {error && <div className="text-xs mt-2" style={{ color: '#ef4444' }}>{error}</div>}
    </div>
  );
}
